import { z } from 'zod';
import LangEnum from '../../../lang/LangEnum';
import { AvailabilityEnum } from '../../products/ProductTypes';
import { isPreOrder } from '../../products/utils/availability.utils';
import { TranslateType } from '../../translation/translationContext.types';
import { CatalogSectionSortCriterionEnum } from '../enums/catalogSectionSortCriterion.enum';
import { CriteriaOperatorEnum } from '../enums/criteriaOperator.enum';
import { NavigationColorMode } from '../enums/navigationColorMode.enum';
import { isYoutubeVideoUrl } from '../products/CatalogScreen/catalogLiveShop.utils';
import { CATALOG_BANNERS_MAX_ITEMS } from './catalogCreationMedia.consts';

export const MIN_AVAILABILITY_CONDITION_VALUE = 1;

const emptyStringToNull = (value: unknown) => (value === '' ? null : value);

const buildOptionalPieceCountSchema = (translate: TranslateType) =>
  z.preprocess(
    emptyStringToNull,
    z
      .int(translate(LangEnum.GLOBAL_INVALID_NUMBER))
      .min(
        MIN_AVAILABILITY_CONDITION_VALUE,
        translate(LangEnum.GLOBAL_VALUE_MUST_BE_GREATER_THAN_OR_EQUAL, {
          value: MIN_AVAILABILITY_CONDITION_VALUE,
        }),
      )
      .max(Number.MAX_SAFE_INTEGER, translate(LangEnum.GLOBAL_INVALID_NUMBER))
      .nullish(),
  );

const buildAvailabilityConditionSchema = (translate: TranslateType) =>
  z.object({
    minTotalPieces: buildOptionalPieceCountSchema(translate),
    minSequentialSizes: buildOptionalPieceCountSchema(translate),
  });

const catalogAccessEntitySchema = z.object({
  id: z.string(),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
});

const buildCatalogCreationFormObjectSchema = (translate: TranslateType) => {
  const availabilityConditionSchema = buildAvailabilityConditionSchema(translate);

  return z.object({
    name: z.string().trim().min(1, translate(LangEnum.GLOBAL_REQUIRED)),
    code: z.string().trim().min(1, translate(LangEnum.GLOBAL_REQUIRED)),
    startDate: z.date(translate(LangEnum.GLOBAL_INVALID_DATE)).nullable(),
    endDate: z.date(translate(LangEnum.GLOBAL_INVALID_DATE)).nullable(),
    enablePublicSharing: z.boolean(),
    liveLink: z
      .string()
      .trim()
      .nullable()
      .refine(
        (value) => !value || isYoutubeVideoUrl(value),
        translate(LangEnum.CATALOG_LIVE_SHOP_INVALID_LINK),
      ),

    imageUrl: z.string().trim().nullable(),
    ribbonBannerUrls: z.array(z.string().trim()).max(CATALOG_BANNERS_MAX_ITEMS),
    ribbonBannerMobileUrls: z.array(z.string().trim()).max(CATALOG_BANNERS_MAX_ITEMS),

    isDynamic: z.boolean(),
    withMedia: z.boolean(),
    availability: z.array(z.enum(AvailabilityEnum)),
    collectionIds: z.array(z.string()),
    classificationIds: z.array(z.string()),
    availabilityCriteriaOperator: z.enum(CriteriaOperatorEnum),
    classificationCriteriaOperator: z.enum(CriteriaOperatorEnum),

    availabilityConditions: z.object({
      [AvailabilityEnum.READY_FOR_DELIVERY]: availabilityConditionSchema,
      [AvailabilityEnum.MATERIAL_PRE_ORDER]: availabilityConditionSchema,
      [AvailabilityEnum.FUTURE_STOCK]: availabilityConditionSchema,
    }),

    navigationColorMode: z.enum(NavigationColorMode, translate(LangEnum.GLOBAL_INSERT_VALID_VALUE)),
    showUnavailableProductsForSale: z.boolean(),
    groupColorsInSingleCard: z.boolean(),
    sectionSortCriterion: z.enum(
      CatalogSectionSortCriterionEnum,
      translate(LangEnum.GLOBAL_INSERT_VALID_VALUE),
    ),
    sortTargetId: z.string().nullable(),

    customers: z.array(catalogAccessEntitySchema),
    salesRepresentatives: z.array(catalogAccessEntitySchema),
    classificationPermissions: z.array(catalogAccessEntitySchema),

    navigationAvailabilities: z.array(z.enum(AvailabilityEnum)),
    navigationDeliveryWindowIds: z.array(z.string()),
    navigationFilterRelatedProducts: z.boolean(),
  });
};

export type CatalogCreationFormValues = z.infer<
  ReturnType<typeof buildCatalogCreationFormObjectSchema>
>;

export type CatalogCreationAccessEntityValues = z.infer<typeof catalogAccessEntitySchema>;

export type CatalogCreationAvailabilityConditionValues = z.infer<
  ReturnType<typeof buildAvailabilityConditionSchema>
>;

type CatalogCreationRefinementContext = z.RefinementCtx<CatalogCreationFormValues>;

const DYNAMIC_CRITERIA_FIELDS = ['availability', 'collectionIds', 'classificationIds'] as const;

export const buildCatalogCreationFormSchema = (translate: TranslateType) => {
  const refineEndDate = (
    values: CatalogCreationFormValues,
    ctx: CatalogCreationRefinementContext,
  ) => {
    if (!values.startDate || !values.endDate) {
      return;
    }

    if (values.endDate > values.startDate) {
      return;
    }

    ctx.addIssue({
      code: 'custom',
      path: ['endDate'],
      message: translate(LangEnum.GLOBAL_END_DATE_ERROR),
    });
  };

  const refineDynamicCriteria = (
    values: CatalogCreationFormValues,
    ctx: CatalogCreationRefinementContext,
  ) => {
    if (!values.isDynamic) {
      return;
    }

    const hasCriterion = DYNAMIC_CRITERIA_FIELDS.some((field) => values[field].length > 0);
    if (hasCriterion) {
      return;
    }

    for (const field of DYNAMIC_CRITERIA_FIELDS) {
      ctx.addIssue({
        code: 'custom',
        path: [field],
        message: translate(LangEnum.CATALOGS_CATALOG_FORM_CRITERIA),
      });
    }
  };

  const refineNavigationDeliveryWindows = (
    values: CatalogCreationFormValues,
    ctx: CatalogCreationRefinementContext,
  ) => {
    if (values.navigationDeliveryWindowIds.length === 0) {
      return;
    }

    if (values.navigationAvailabilities.some(isPreOrder)) {
      return;
    }

    ctx.addIssue({
      code: 'custom',
      path: ['navigationDeliveryWindowIds'],
      message: translate(
        LangEnum.CATALOGS_NAVIGATION_PREFERENCES_DELIVERY_WINDOWS_AVAILABILITIES_ERROR,
      ),
    });
  };

  return buildCatalogCreationFormObjectSchema(translate).superRefine((values, ctx) => {
    refineEndDate(values, ctx);
    refineDynamicCriteria(values, ctx);
    refineNavigationDeliveryWindows(values, ctx);
  });
};
